import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Avatar,
  Badge,
  Chip,
} from "@mui/material";
import {
  AdminPanelSettings,
  Groups,
  Analytics,
  Settings,
  Logout,
  MenuOpen,
  NotificationsActive,
  Security,
  Assignment,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";

interface SidebarProps {
  sidebarOpen: boolean;
  setActiveComponent: (component: string) => void;
  toggleSidebar: () => void;
}

const AdminSidebar: React.FC<SidebarProps> = ({
  sidebarOpen,
  setActiveComponent,
  toggleSidebar,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeComponent, setActiveComponentState] =
    useState<string>("AdminCarriers");
  const [notificationCount] = useState(3);

  const handleItemClick = (component: string) => {
    setActiveComponentState(component);
    setActiveComponent(component);
    if (isMobile) {
      toggleSidebar();
    }
  };

  const adminItems = [
    // { text: "Dashboard", icon: <DashboardIcon />, component: "Dashboard" },
    {
      text: "Carriers",
      icon: <Groups />,
      component: "AdminCarriers",
    },
    // { text: "Analytics", icon: <Analytics />, component: "Analytics" },
    // { text: "Security", icon: <Security />, component: "Security" },
    // { text: "Audit Logs", icon: <Assignment />, component: "AuditLogs" },
  ];

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={sidebarOpen}
      onClose={toggleSidebar}
      sx={{
        width: sidebarOpen ? 280 : 72,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: sidebarOpen ? 280 : 72,
          boxSizing: "border-box",
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(isMobile && {
            position: "fixed",
            zIndex: theme.zIndex.drawer + 2,
          }),
          overflowX: "hidden",
        },
      }}
    >
      {/* Collapse Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          p: 1,
          borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : "",
          mb: "40px",
        }}
      >
        {isMobile && (
          <IconButton onClick={toggleSidebar}>
            <MenuOpen fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Admin Profile */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <Chip
              label="Admin"
              size="small"
              color="primary"
              sx={{
                height: 16,
                fontSize: "0.6rem",
                "& .MuiChip-label": { px: 0.5 },
              }}
            />
          }
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: theme.palette.primary.main,
            }}
          >
            <AdminPanelSettings fontSize="large" />
          </Avatar>
        </Badge>

        {sidebarOpen && (
          <>
            <Typography variant="subtitle1" fontWeight="medium">
              Admin User
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Super Admin Access
            </Typography>
          </>
        )}
      </Box>

      {/* Main Navigation */}
      <List sx={{ flexGrow: 1 }}>
        {adminItems.map(({ text, icon, component }) => (
          <ListItem
            key={component}
            disablePadding
            sx={{
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemButton
              selected={activeComponent === component}
              onClick={() => handleItemClick(component)}
              sx={{
                px: sidebarOpen ? 3 : 2.5,
                py: 1.25,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.action.selected,
                  borderRight: `3px solid ${theme.palette.primary.main}`,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: theme.palette.action.selected,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarOpen ? 40 : "auto" }}>
                <Badge color="error">{icon}</Badge>
              </ListItemIcon>
              {sidebarOpen && <ListItemText primary={text} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
        <List>
          {/* <ListItem disablePadding>
            <ListItemButton
              sx={{ px: 3, py: 1.25 }}
              onClick={() => handleItemClick("Settings")}
            >
              <ListItemIcon sx={{ minWidth: sidebarOpen ? 40 : "auto" }}>
                <Settings />
              </ListItemIcon>
              {sidebarOpen && <ListItemText primary="Settings" />}
            </ListItemButton>
          </ListItem> */}
          <ListItem disablePadding>
            <ListItemButton
              sx={{ px: 3, py: 1.25 }}
              onClick={() => {
                localStorage.clear();
                window.location.href = "/admin-login";
              }}
            >
              <ListItemIcon sx={{ minWidth: sidebarOpen ? 40 : "auto" }}>
                <Logout />
              </ListItemIcon>
              {sidebarOpen && <ListItemText primary="Logout" />}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default AdminSidebar;
